import { useEffect, useState, useCallback } from "react";
import FleetMap from "@/components/map/FleetMap";
import AssetDetailPanel from "@/components/map/AssetDetailPanel";
import FleetLiveFeed from "@/components/map/FleetLiveFeed";
import ZonePanel from "@/components/map/ZonePanel";
import AlertsFeed from "@/components/map/AlertsFeed";
import { fleetApi, zonesApi, alertsApi } from "@/lib/api";
import { useFleetWebSocket } from "@/hooks/useFleetWebSocket";
import { STATUS_CONFIG } from "@/lib/constants";
import { CircleNotch, Pulse, Crosshair, WifiHigh, WifiSlash, Path } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function LiveMapPage() {
  const { assets, alertEvents, connected, lastTick, setAssets } = useFleetWebSocket([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zones, setZones] = useState([]);
  const [drawing, setDrawing] = useState(false);
  const [pendingPolygon, setPendingPolygon] = useState([]);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [showTrail, setShowTrail] = useState(false);
  const [trail, setTrail] = useState(null);

  // Initial load (fallback in case WS hasn't snapshotted yet)
  useEffect(() => {
    const init = async () => {
      try {
        const [a, z, al] = await Promise.all([
          fleetApi.list(),
          zonesApi.list(),
          alertsApi.list({ only_unack: true, limit: 20 }),
        ]);
        if (a.length > 0) setAssets(a);
        setZones(z);
        setActiveAlerts(al);
      } catch (e) {
        // noop
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [setAssets]);

  // Merge WebSocket alert events into activeAlerts state
  useEffect(() => {
    if (alertEvents && alertEvents.length > 0) {
      setActiveAlerts((prev) => {
        const existing = new Set(prev.map((a) => a.id));
        const fresh = alertEvents.filter((a) => !existing.has(a.id));
        if (fresh.length > 0) {
          fresh.forEach((a) => {
            toast.warning(`${a.asset_name} ${a.event === "entry" ? "entered" : "exited"} ${a.zone_name}`);
          });
        }
        return [...fresh, ...prev].slice(0, 50);
      });
    }
  }, [alertEvents]);

  // Keep selected asset in sync with live data
  useEffect(() => {
    if (selected) {
      const updated = assets.find((a) => a.id === selected.id);
      if (updated) setSelected(updated);
    }
  }, [assets]); // eslint-disable-line

  // Load trail when toggled or selected changes
  useEffect(() => {
    const load = async () => {
      if (!showTrail || !selected) { setTrail(null); return; }
      try {
        const data = await fleetApi.trail(selected.id, 1);
        setTrail(data);
      } catch (e) {
        console.warn("[LiveMapPage] failed to load trail for", selected?.id, e);
        setTrail(null);
      }
    };
    load();
  }, [showTrail, selected]);

  const handleStartDraw = useCallback(() => {
    setDrawing(true);
    setPendingPolygon([]);
  }, []);

  const handleAddPoint = useCallback((pt) => {
    setPendingPolygon((prev) => [...prev, pt]);
  }, []);

  const handleFinishDraw = useCallback(() => {
    // Open form by leaving drawing state; ZonePanel reads pendingPolygon
    if (pendingPolygon.length < 3) {
      toast.error("Need at least 3 points to define a zone");
      return;
    }
    setDrawing(false);
  }, [pendingPolygon]);

  const handleCancelDraw = useCallback(() => {
    setDrawing(false);
    setPendingPolygon([]);
  }, []);

  const handleCreateZone = async (data) => {
    const z = await zonesApi.create(data);
    setZones((prev) => [...prev, z]);
    setPendingPolygon([]);
    setDrawing(false);
  };

  const handleDeleteZone = async (id) => {
    try {
      await zonesApi.remove(id);
      setZones((prev) => prev.filter((z) => z.id !== id));
      toast.success("Zone deleted");
    } catch (e) {
      toast.error("Failed to delete zone");
    }
  };

  const refreshAlerts = async () => {
    const al = await alertsApi.list({ only_unack: true, limit: 20 });
    setActiveAlerts(al);
  };

  const statusCounts = assets.reduce((acc, a) => {
    acc[a.status] = (acc[a.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="h-[calc(100vh-72px)] relative ll-grid-bg" data-testid="live-map-page">
      {loading ? (
        <div className="h-full flex items-center justify-center">
          <CircleNotch size={32} className="animate-spin text-[#ff8c00]" />
        </div>
      ) : (
        <>
          <div className="absolute inset-0">
            <FleetMap
              assets={assets}
              selectedId={selected?.id}
              onSelect={setSelected}
              zones={zones}
              drawing={drawing}
              pendingPolygon={pendingPolygon}
              onAddDrawPoint={handleAddPoint}
              onFinishDraw={handleFinishDraw}
              trail={showTrail ? trail : null}
            />
          </div>

          {/* Status Overlay */}
          <div className="absolute top-4 left-4 z-[1000] pointer-events-none space-y-3">
            <div className="bg-black/70 backdrop-blur-xl border border-white/10 p-4 min-w-[280px] pointer-events-auto" data-testid="status-overlay">
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-white/10">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">Fleet Status</div>
                  <div className="font-heading font-black text-2xl tracking-tight mt-1">
                    {assets.length} <span className="text-sm text-[#a1a1aa] font-normal">UNITS</span>
                  </div>
                </div>
                <div className="flex items-center gap-2" style={{ color: connected ? "#22c55e" : "#ef4444" }}>
                  {connected ? <WifiHigh size={16} weight="fill" /> : <WifiSlash size={16} weight="fill" />}
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold">
                    {connected ? "WS LIVE" : "RECONNECTING"}
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                  <div key={key} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2" style={{ background: cfg.color }}></span>
                      <span className="text-[#a1a1aa] uppercase tracking-wider">{cfg.label}</span>
                    </div>
                    <span className="font-mono font-bold" style={{ color: cfg.color }}>
                      {String(statusCounts[key] || 0).padStart(2, "0")}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2 text-[10px] text-[#71717a] uppercase tracking-[0.2em]">
                <Crosshair size={12} />
                <span>Last tick: {lastTick ? lastTick.toLocaleTimeString() : "—"}</span>
              </div>
            </div>

            <div className="pointer-events-auto">
              <ZonePanel
                zones={zones}
                onCreate={handleCreateZone}
                onDelete={handleDeleteZone}
                drawing={drawing}
                onStartDraw={handleStartDraw}
                onCancelDraw={handleCancelDraw}
                pendingPolygon={pendingPolygon}
              />
            </div>
          </div>

          {/* Alerts Feed (top-right just above asset panel) */}
          {!selected && (
            <div className="absolute top-4 right-4 z-[1000]">
              <AlertsFeed alerts={activeAlerts} onRefresh={refreshAlerts} />
            </div>
          )}

          {/* Live Feed (bottom-left) */}
          <div className="absolute bottom-4 left-4 z-[1000] hidden lg:block">
            <FleetLiveFeed assets={assets} onSelect={setSelected} selectedId={selected?.id} />
          </div>

          {/* Asset Detail Panel */}
          {selected && (
            <div className="absolute top-4 right-4 bottom-4 z-[1000] w-[360px] max-w-[calc(100vw-32px)] flex flex-col gap-3">
              <AssetDetailPanel
                asset={selected}
                onClose={() => { setSelected(null); setShowTrail(false); }}
                onToggleTrail={() => setShowTrail((s) => !s)}
                showTrail={showTrail}
              />
            </div>
          )}

          {/* Trail mode indicator */}
          {showTrail && selected && (
            <div className="absolute bottom-4 right-[388px] z-[1000] bg-black/70 backdrop-blur-xl border border-[#4682b4] px-3 py-2 flex items-center gap-2" data-testid="trail-indicator">
              <Path size={14} className="text-[#4682b4]" />
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#4682b4]">Trail Mode · Last Hour</span>
            </div>
          )}

          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="ll-scan-line"></div>
          </div>
        </>
      )}
    </div>
  );
}
